
import { Component, Vue } from 'vue-property-decorator'
import { fetchCart } from '@/api/Cart'
import store from '@/store/store'
import { TixTime } from '@/TixTime/TixTime'
import { API } from '@/api/API'
import { apiErrorMessage, logInTrackJS } from '@/errors/helpers'
import ExpiryTimer from '@/components/cart/ExpiryTimer.vue'
import type { TixTimeDuration } from '@/TixTime/Duration'
import { cartExpiresIn, cartExpiryAlertThreshold } from '@/state/Cart'
import type { LanguageStrings } from '@/language/types'

@Component({ name: 'ExtendCartTimerModal', components: { ExpiryTimer } })
export default class extends Vue {
  expiresIn: TixTimeDuration | null = null
  expiryInterval: ReturnType<typeof setTimeout>
  error: null | string = null
  t: LanguageStrings['extendCartTimerModal']
  submitting: boolean = false

  created() {
    this.setExpiry()
    this.expiryInterval = setInterval(() => {
      const expiresInSeconds = this.expiresIn?.asSeconds() ?? 0
      // Ensures that all tabs close their modal instances,
      // preventing the user from extending the cart timer multiple times consecutively.
      if (expiresInSeconds > cartExpiryAlertThreshold || store.getters['Cart/declinedCartTimerExtension']) {
        this.close()
      } else {
        this.setExpiry()
      }
    }, 1000)
  }

  setExpiry() {
    this.expiresIn = cartExpiresIn()
  }

  destroyed() {
    clearInterval(this.expiryInterval)
  }

  close() {
    this.$modals.close({ name: 'extend-cart-timer-modal' })
  }

  dismiss() {
    store.commit('Cart/declinedCartTimerExtension', true)
    this.close()
  }

  confirm() {
    const cart = store.getters['Cart/cart']
    const extended = new TixTime(cart?.expires_at).add(2, 'minutes')
    this.submitting = true
    return (
      API.patch(`my/cart/${cart.id}`, {
        body: {
          expires_at: extended.format(),
        },
      })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- PATCH response has expires_in but doesn't match full ApiResponse type
        .then((response: any) => {
          const localExpiry = new TixTime().add(response.expires_in - 5, 'seconds').asTimeValue()

          // The response from PATCH request does not contain the full CartApiResponse,
          // so we need a fetch here.
          return fetchCart(cart.id).then((response) => {
            // It's too late by now and the cart has already expired.
            // This can happen when a user attempts to extend the cart timer on a slow connection and
            // when the cart almost expires
            if (this.$root.expiryModalOpen) return

            store.commit('Cart/localExpiry', localExpiry)
            store.commit('Cart/apiResponse', response)

            this.close()
          })
        })
        .catch((err) => {
          this.error = apiErrorMessage(err) as string
          logInTrackJS(this.error as string, { cartId: cart?.id })
        })
        .finally(() => {
          this.submitting = false
        })
    )
  }
}
